import { defaultNotificationOptionsSettingValue } from '../../backend/config/settings/NotificationOptionsDefaultValue';
import { baseClientDefaults } from '@juvare/platform-sdk-js/BaseClient';
import { useFunctionClient } from '@juvare/platform-sdk-react/ui/hooks';
import React from 'react';
import { alertPrefix } from '../clients/AlertServiceClient';
import AlertsClient from '../clients/AlertsClient';
import ContextClient from '../clients/ContextClient';
import DocumentClient from '../clients/DocumentClient';
import ReportClient from '../clients/ReportClient';
import { useAlertConfig } from './useAlertConfig';
import { useUserState } from './useUserState';
// eslint-disable-next-line no-restricted-imports
export * from '../features/alerts/hooks/useSearchAlertResponders';
// eslint-disable-next-line no-restricted-imports
export * from '../features/alerts/hooks/useTotalRecipients';
export * from './useAlertConfig';
export * from './useAlertUiTranslation';
export * from './useAlertsRoutes';
export * from './useUserState';
function checkAppConfig(alertConfig) {
    if (!alertConfig) {
        throw new Error('Make sure code is wrapped with AlertConfigProvider');
    }
}
export function useAlertClient() {
    const config = useAlertConfig();
    const userState = useUserState();
    checkAppConfig(config);
    return React.useMemo(() => new AlertsClient(baseClientDefaults.baseUrl + alertPrefix + '/api'), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userState.id]);
}
export function useContextClient() {
    const config = useAlertConfig();
    const userState = useUserState();
    checkAppConfig(config);
    return React.useMemo(() => new ContextClient(baseClientDefaults.baseUrl + alertPrefix + '/api'), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userState.id]);
}
export function useAlertStorageClient() {
    var _a;
    const functionClient = useFunctionClient();
    const { notificationOptions, stateCode } = useAlertConfig();
    const secureMessageHint = (notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.secureMessageHint)
        ? notificationOptions.secureMessageHint
        : defaultNotificationOptionsSettingValue.secureMessageHint;
    const genericMessage = (notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.genericSensitiveMessageHint)
        ? notificationOptions.genericSensitiveMessageHint
        : defaultNotificationOptionsSettingValue.genericSensitiveMessageHint;
    const phinHtmlMessageOptions = (notificationOptions === null || notificationOptions === void 0 ? void 0 : notificationOptions.phinOptions) && ((_a = notificationOptions.phinOptions) === null || _a === void 0 ? void 0 : _a.htmlMessageOptions)
        ? notificationOptions.phinOptions.htmlMessageOptions
        : defaultNotificationOptionsSettingValue.phinOptions.htmlMessageOptions;
    const saveAlert = React.useCallback((alert) => functionClient
        .executeFunction({
        functionName: 'saveAlert',
        params: {
            alert,
            secureMessageHint,
            genericMessage,
            phinHtmlMessageOptions,
            stateCode,
        },
    })
        .then((response) => response.result), [stateCode, functionClient, secureMessageHint, phinHtmlMessageOptions, genericMessage]);
    const updateAlert = React.useCallback((alert) => functionClient
        .executeFunction({
        functionName: 'updateAlert',
        params: {
            alert,
            secureMessageHint,
            genericMessage,
            phinHtmlMessageOptions,
            stateCode,
        },
    })
        .then((response) => response.result), [stateCode, functionClient, secureMessageHint, phinHtmlMessageOptions, genericMessage]);
    const validate = React.useCallback((alert) => functionClient
        .executeFunction({
        functionName: 'validate',
        params: {
            alert,
            secureMessageHint,
            genericMessage,
            phinHtmlMessageOptions,
            stateCode,
        },
    })
        .then((response) => response.result), [stateCode, functionClient, secureMessageHint, phinHtmlMessageOptions, genericMessage]);
    const sendAlert = React.useCallback((alert) => functionClient
        .executeFunction({
        functionName: 'sendAlert',
        params: {
            alert,
            secureMessageHint,
            genericMessage,
            phinHtmlMessageOptions,
            stateCode,
        },
    })
        .then((response) => response.result), [stateCode, functionClient, secureMessageHint, phinHtmlMessageOptions, genericMessage]);
    const sendTestAlert = React.useCallback((alert, channelAddresses) => functionClient
        .executeFunction({
        functionName: 'sendTestAlert',
        params: {
            alert,
            channelAddresses,
            secureMessageHint,
            genericMessage,
            phinHtmlMessageOptions,
            stateCode,
        },
    })
        .then((response) => response.result), [stateCode, functionClient, secureMessageHint, phinHtmlMessageOptions, genericMessage]);
    return { saveAlert, updateAlert, validate, sendAlert, sendTestAlert };
}
export function useDocumentClient() {
    const config = useAlertConfig();
    const userState = useUserState();
    checkAppConfig(config);
    return React.useMemo(() => new DocumentClient(baseClientDefaults.baseUrl + alertPrefix + '/api'), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userState.id]);
}
export function useReportClient() {
    const config = useAlertConfig();
    const userState = useUserState();
    checkAppConfig(config);
    return React.useMemo(() => new ReportClient(baseClientDefaults.baseUrl + alertPrefix + '/api'), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userState.id]);
}
